import { formatCurrency } from '@angular/common';
import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { combineLatest, Observable, OperatorFunction } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { RecalculationService } from './recalculation.service';
import { Controller } from '../../../Controller';
import {
  TermQuotePremiumBreakdown,
  WholeLifeQuotePremiumBreakdown,
} from '../../../../../../coverpath-common/js/interfaces/CoverpathTypes';
import { PremiumCalculationService } from '../../../../../../coverpath-common/angular/services/validation/premium-calculation.service';
import { POLICY_TYPES } from '../../../../../../common/js/constants/policy';

@Injectable()
export class TotalPremiumService {
  controller: Controller;
  isQuotePageV2Enabled: boolean = false;
  constructor(private recalculationService: RecalculationService) {}

  // Given a root control, finds the whole life control associated with the current form
  public getWholeLifeControl(control: AbstractControl, isQuotePageV2Enabled?: boolean): AbstractControl {
    let result = control;

    if (
      (control.value.jointAppQuote && control.value.productType !== 'Whole') ||
      (isQuotePageV2Enabled && control['controls'].jointAppQuote && control['controls'].jointAppQuote.wholeLifeQuote)
    ) {
      result = control.get('jointAppQuote.wholeLifeQuote');
    } else if (control.value.wholeLifeQuote || (isQuotePageV2Enabled && control['controls'].wholeLifeQuote)) {
      result = control.get('wholeLifeQuote');
    }

    return result;
  }

  public getWholeLifePremiumBreakdown(
    control: AbstractControl,
    isQuotePageV2Enabled?: boolean
  ): WholeLifeQuotePremiumBreakdown {
    control = this.getWholeLifeControl(control, isQuotePageV2Enabled);
    return PremiumCalculationService.calculateWholeLifePremium({
      paymentFrequency: control.get('paymentFrequency').value,
      basePremium: control.get('premium').value,
      annualBasePremium: control.get('annualPremium').value,
      hasWaiver: control.get('waiverOfPremium').value,
      waiver: control.get('waiver').value,
      hasGirRider: control.get('riders.gir.enabled').value,
      girPremium: control.get('riders.gir.premium').value,
      girWaiver: control.get('riders.gir.waiver').value,
      hasRtrRider: control.get('riders.rtr.enabled').value,
      rtrPremium: control.get('riders.rtr.premium').value,
      rtrWaiver: control.get('riders.rtr.waiver').value,
      flatExtras: control.get('flatExtras').value,
      rtrFlatExtras: control.get('riders.rtr.flatExtras').value,
      productCode: control.get('product').value,
      alirEnabled: control.get('riders.alir.enabled').value,
      alirAmount: control.get('riders.alir.amount').value,
      alirPremium: null,
      hasCirRider: control.get('riders.cir.enabled').value,
      cirPremium: control.get('riders.cir.premium').value,
      cirWaiver: control.get('riders.cir.waiver').value,
      totalPremium: null,
    });
  }

  public getWholeLifePremiumBreakdown$(
    control: AbstractControl,
    isQuotePageV2Enabled?: boolean
  ): Observable<WholeLifeQuotePremiumBreakdown> {
    control = this.getWholeLifeControl(control, isQuotePageV2Enabled);
    const controlsToWatch = [
      control.get('paymentFrequency'),
      control.get('premium'),
      control.get('annualPremium'),
      control.get('waiverOfPremium'),
      control.get('waiver'),
      control.get('riders.gir.enabled'),
      control.get('riders.gir.premium'),
      control.get('riders.gir.waiver'),
      control.get('riders.rtr.enabled'),
      control.get('riders.rtr.premium'),
      control.get('riders.rtr.waiver'),
      control.get('flatExtras'),
      control.get('riders.rtr.flatExtras'),
      control.get('product'),
      control.get('riders.alir.enabled'),
      control.get('riders.alir.amount'),
      control.get('exchange1035.enabled'),
      control.get('exchange1035.applyTowards'),
      control.get('exchange1035.cashValueTransfer'),
      control.get('riders.cir.enabled'),
      control.get('riders.cir.premium'),
      control.get('riders.cir.waiver'),
    ];
    const valueChanges = controlsToWatch.map((control) =>
      control.valueChanges.pipe(startWith(control.value), distinctUntilChanged())
    );
    return combineLatest(valueChanges).pipe(map(() => this.getWholeLifePremiumBreakdown(control)));
  }

  public getTermLifePremiumBreakdown$(control: AbstractControl): Observable<TermQuotePremiumBreakdown> {
    if (control.value.jointAppQuote) {
      control = control.get('jointAppQuote.termQuote');
    } else if (control.value.termQuote) {
      control = control.get('termQuote');
    }

    const controls = [
      control.get('premium'),
      control.get('waiverOfPremium'),
      control.get('waiver'),
      control.get('flatExtras'),
      control.get('coverageTerm'),
    ];
    const valueChanges = controls.map((control) =>
      control.valueChanges.pipe(startWith(control.value), distinctUntilChanged())
    );
    return combineLatest(valueChanges).pipe(
      map(([basePremium, hasWaiver, waiver, flatExtras, coverageTerm]) =>
        PremiumCalculationService.calculateTermLifePremium({
          basePremium,
          hasWaiver,
          waiver,
          flatExtras,
          coverageTerm,
          totalPremium: null,
        })
      )
    );
  }

  public getTermLifeRepricePremiumBreakdown$(control: AbstractControl): Observable<TermQuotePremiumBreakdown> {
    if (control.value.jointAppQuote) {
      control = control.get('jointAppQuote.termQuote');
    } else if (control.value.termQuote) {
      control = control.get('termQuote');
    }

    const controls = [
      control.get('repricePremium'),
      control.get('waiverOfPremium'),
      control.get('repriceWaiver'),
      control.get('repriceFlatExtras'),
      control.get('coverageTerm'),
      control.get('useReprice'),
    ];
    const valueChanges = controls.map((control) =>
      control.valueChanges.pipe(startWith(control.value), distinctUntilChanged())
    );
    return combineLatest(valueChanges).pipe(
      map(([basePremium, hasWaiver, waiver, flatExtras, coverageTerm]) =>
        PremiumCalculationService.calculateTermLifePremium({
          basePremium,
          hasWaiver,
          waiver,
          flatExtras,
          coverageTerm,
          totalPremium: null,
        })
      )
    );
  }

  public getTotalTermPremium$(control: AbstractControl): Observable<number> {
    const formData$ = this.getTermLifePremiumBreakdown$(control);
    return combineLatest(formData$).pipe(map(([breakdown]) => breakdown.totalPremium));
  }

  public getTotalTermRepricePremium$(control: AbstractControl): Observable<number> {
    const formData$ = this.getTermLifeRepricePremiumBreakdown$(control);
    return combineLatest(formData$).pipe(map(([breakdown]) => breakdown.totalPremium));
  }

  public getTotalTermPremiumFormatted$(control: AbstractControl): Observable<string> {
    return this.getTotalTermPremium$(control).pipe(this.normalizeAndFormat());
  }

  public getTotalTermRepricePremiumFormatted$(control: AbstractControl): Observable<string> {
    return this.getTotalTermRepricePremium$(control).pipe(this.normalizeAndFormat());
  }

  public getTotalWholeLifePremium(control: AbstractControl): number {
    return this.getWholeLifePremiumBreakdown(control).totalPremium;
  }

  public getTotalWholeLifePremium$(control: AbstractControl): Observable<number> {
    const formData$ = this.getWholeLifePremiumBreakdown$(control);
    return combineLatest(formData$).pipe(map(([breakdown]) => breakdown.totalPremium));
  }

  public getTotalWholeLifePremiumFormatted$(control: AbstractControl): Observable<string> {
    return this.getTotalWholeLifePremium$(control).pipe(this.normalizeAndFormat());
  }

  public getTotalJointAppPremiumFormatted$(control: AbstractControl): Observable<string> {
    return this.getTotalJointAppPremium$(control).pipe(this.normalizeAndFormat());
  }

  public getTotalJointAppPremium$(control: AbstractControl): Observable<number> {
    const termPremium$ = this.getTotalTermPremium$(control.get('termQuote'));
    const termRepricePremium$ = this.getTotalTermRepricePremium$(control.get('termQuote'));
    const wholePremium$ = this.getTotalWholeLifePremium$(control.get('wholeLifeQuote'));
    return combineLatest(termPremium$, termRepricePremium$, wholePremium$).pipe(
      map(([term, termReprice, whole]) => {
        if (control.get('termQuote.type').value === POLICY_TYPES.AMEND_REMOVE_SECONDARY) {
          term = 0;
          termReprice = 0;
        }
        if (control.get('wholeLifeQuote.type').value === POLICY_TYPES.AMEND_REMOVE_PRIMARY) {
          whole = 0;
        }

        if (control.get('termQuote.useReprice').value === true) {
          if ((termReprice === 0 || termReprice) && (whole === 0 || whole)) {
            return termReprice + whole;
          }
        } else {
          if ((term === 0 || term) && (whole === 0 || whole)) {
            return term + whole;
          }
        }
        return undefined;
      })
    );
  }

  /**
   * This rxjs operator converts the given stream of numeric premium values to formatted values.
   *
   * Logic:
   * If the premium is loading from server, returns null.
   * Else, returns US currency formatted value.
   *
   **/
  private normalizeAndFormat(): OperatorFunction<number, string> {
    const FORMAT_PLACEHOLDER = '-';
    const { loading$ } = this.recalculationService;

    function format(premium: number): string {
      return premium ? formatCurrency(premium, 'en-US', '$') : FORMAT_PLACEHOLDER;
    }

    return (source$: Observable<number>): Observable<string> =>
      combineLatest(source$, loading$).pipe(map(([premium, loading]) => (loading ? null : format(premium))));
  }
}
